import { ProfileRoleMap } from "@qendo/profile";
import type { JoinInvitationData } from "@qendo/profile/invitation";
import type { AxiosResponse } from "axios";
import { useGlobalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, View } from "react-native";
import { Button } from "~/components/Button";
import { AlertModal } from "~/components/feedback/AlertModal";
import { GuestFieldGroup } from "~/components/layout/guest/GuestFieldGroup";
import { Page } from "~/components/layout/Page";
import { Spacer } from "~/components/layout/Spacer";
import { BodyText, Subtitle, Title } from "~/components/layout/text";
import { Only } from "~/components/permission/Only";
import { Gap, Items, Padding } from "~/constants/layout";
import { type ApiError, useApi } from "~/providers/ApiProvider";
import { useDismiss } from "~/providers/DismissProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { useUser } from "~/providers/UserProvider";
import { empty, filled } from "~/util";

export default function Invitation() {
    const { token }: { token: string } = useGlobalSearchParams();
    const { colours } = useTheme();
    const { client } = useApi();
    const { handleDismiss } = useDismiss();
    const { acceptInvite } = useUser();
    const { changeProfile } = useProfile();

    const { loading, setLoading } = useFeedback();
    const [invite, setInvite] = useState<JoinInvitationData | null>(null);
    const [message, setMessage] = useState<string | null>(null);

    const handleError = (error: ApiError) => {
        switch (error.status) {
            case 404:
                setMessage("This invitation cannot be found or may have expired.");
                return;
            case 403:
                setMessage("You are already a member of this team.");
                return;
        }

        setMessage(error.response?.data?.message ?? "We could not find this invitation");
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(handleError): error handler
    // biome-ignore lint/correctness/useExhaustiveDependencies(client): API
    // biome-ignore lint/correctness/useExhaustiveDependencies(setLoading): feedback
    useEffect(() => {
        setLoading(true);
        setMessage(null);
        setInvite(null);

        client()
            .get(`/invitation/${token}`)
            .then((response: AxiosResponse<JoinInvitationData>) => {
                setInvite(response.data);
            })
            .catch(handleError)
            .finally(() => setLoading(false));
    }, [token]);

    const join = () => {
        if (loading || empty(invite) || filled(message)) {
            return;
        }

        setLoading(true);

        acceptInvite(token)
            .then((profile_id) => {
                setTimeout(() => {
                    changeProfile(profile_id);
                    handleDismiss();
                }, 10);
            })
            .catch(handleError)
            .finally(() => setLoading(false));
    };

    return (
        <Page gradient spacer>
            <Only when={empty(invite) && empty(message)}>
                <View style={[Items.center, Gap.xxl]}>
                    <ActivityIndicator size="large" />
                    <Subtitle>Looking up invitation details...</Subtitle>
                </View>
            </Only>
            {filled(invite) && (
                <>
                    <GuestFieldGroup>
                        <Title size="xl">Join my QENDO team</Title>
                        <BodyText>{invite.profile_name} has invited you to join their QENDO team!</BodyText>
                        <BodyText>
                            If you wish to join as a {ProfileRoleMap[invite.role]} team member, press &#34;Join
                            Team&#34; below.
                        </BodyText>
                    </GuestFieldGroup>
                    <Spacer />
                    <View style={[Padding.bottom_xxl, Items.center, Gap.sm]}>
                        <Button
                            variant="primary"
                            size="lg"
                            title="Join my team!"
                            loading={loading}
                            onPress={join}
                            disabled={filled(message) || loading}
                        />
                        <Button variant="quiet" size="sm" title="Maybe later" onPress={handleDismiss} />
                    </View>
                </>
            )}
            {filled(message) && (
                <AlertModal
                    title="Error"
                    description={message}
                    icon="warning-outline"
                    iconColour={colours.icon.danger}
                    dismissable
                    dismissableBackButton
                    onDismiss={handleDismiss}
                />
            )}
        </Page>
    );
}
